import { Link, useOutletContext } from "react-router-dom";

import { OutletContextType } from "../../types/GlobalType";

import { useState, useReducer } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { userController } from "../../_api/controllers/user.controller.ts";

import { personalController } from "../../_api/controllers/personal.controller.ts";

import { roleController } from "../../_api/controllers/role.controller.ts";

import {
  createUserSchema,
  updateUserPasswordSchema,
} from "../../validators/userValidators.ts";

import z from "zod";

//skeleton component

const UsersManagementSkeleton = ({
  isDarkMode,

  rows = 6,
}: {
  isDarkMode: boolean;

  rows?: number;
}) => {
  const skeleton = isDarkMode ? "bg-gray-700" : "bg-gray-300";

  const bgCard = isDarkMode ? "bg-[#0D1117]" : "bg-white";

  const bgHeader = isDarkMode ? "bg-[#161B22]" : "bg-gray-50";

  const border = isDarkMode ? "border-gray-700" : "border-gray-300";

  return (
    <div className="space-y-6 animate-pulse">
      {/* ===== HEADER ===== */}

      <div className="flex justify-between items-center">
        <div>
          <div className={`h-6 w-64 rounded mb-3 ${skeleton}`} />

          <div className={`h-4 w-96 rounded ${skeleton}`} />
        </div>

        <div className={`h-8 w-48 rounded ${skeleton}`} />
      </div>

      {/* ===== TABLE ===== */}

      <div className={`rounded-lg overflow-hidden border ${border}`}>
        {/* Header */}

        <div className={`grid grid-cols-5 gap-4 px-6 py-3 ${bgHeader}`}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`h-3 rounded ${skeleton}`} />
          ))}
        </div>

        {/* Rows */}

        <div className={`${bgCard}`}>
          {[...Array(rows)].map((_, index) => (
            <div
              key={index}
              className={`grid grid-cols-5 gap-4 px-6 py-4 border-t ${border}`}
            >
              {/* User */}

              <div className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-full ${skeleton}`} />

                <div className="space-y-2">
                  <div className={`h-3 w-32 rounded ${skeleton}`} />

                  <div className={`h-2 w-24 rounded ${skeleton}`} />
                </div>
              </div>

              {/* Email */}

              <div className={`h-3 w-40 rounded ${skeleton}`} />

              {/* Role */}

              <div className={`h-5 w-24 rounded-full ${skeleton}`} />

              {/* Status */}

              <div className={`h-5 w-20 rounded-full ${skeleton}`} />

              {/* Actions */}

              <div className="flex justify-center gap-3">
                <div className={`h-5 w-5 rounded ${skeleton}`} />

                <div className={`h-5 w-5 rounded ${skeleton}`} />

                <div className={`h-5 w-5 rounded ${skeleton}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== FOOTER ===== */}

      <div className={`h-4 w-56 rounded ${skeleton}`} />
    </div>
  );
};

const User: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();

  const [searchQuery, setSearchQuery] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [itemToDelete, setItemToDelete] = useState(null);

  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  const [showNewUserModal, setshowNewUserModal] = useState(false);

  const [showEditUserModal, setshowEditUserModal] = useState(false);

  const [showEditPswModal, setshowEditPswModal] = useState(false);

  //notification

  const [notification, setNotification] = useState<any>(null); // { message, type }

  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });

    setTimeout(() => setNotification(null), 3000);
  };

  const [newUserForm, setNewUserForm] = useState<{
    id?: any;

    personalId: any;

    email: string;

    roleId: any;

    password: string;

    confirmPassword: string;
  }>({
    id: null,

    personalId: null,

    email: "",

    roleId: "",

    password: "",

    confirmPassword: "",
  });

  const [newUserErrors, setNewUserErrors] = useState({
    name: "",

    role: "",

    email: "",

    password: "",

    confirmPassword: "",
  });

  //fetch users

  const { data: userData, isLoading: isUserDataLoading } = useQuery({
    queryKey: ["users"],

    queryFn: userController.getAll,
  });

  //fetch all personals

  const { data: personalData, isLoading: isPersonalDataLoading } = useQuery({
    queryKey: ["personals"],

    queryFn: personalController.getAll,
  });

  //fetch all personals

  const { data: roleData, isLoading: isRoleDataLoading } = useQuery({
    queryKey: ["roles"],

    queryFn: roleController.getAll,
  });

  const userDataArr = userData?.data || [];

  const personalDataArr = personalData?.data || [];

  const roleDataArr = roleData?.data || [];

  const updateUserDataArr = userDataArr.map((user) => {
    const personal = personalDataArr.find(
      (personal) => personal.personnel_id === user.personnel_id,
    );

    const role = roleDataArr.find((role) => role.id === user.role_id);

    const fullName = `${personal?.nom || ""} ${personal?.postnom || ""} ${
      personal?.prenom || ""
    }`.trim();

    const firstInitial = (personal?.nom?.[0] || "").toUpperCase();

    const secondInitial = (personal?.postnom?.[0] || "").toUpperCase();

    const initials = `${firstInitial}${secondInitial}`;

    const roleName = role?.name;

    return {
      ...user,

      initials,

      fullName,

      roleName: roleName || "Inconnu",
    };
  });

  const filteredUsers = updateUserDataArr.filter((user) => {
    const matchesQuery = user.fullName

      .toLowerCase()

      .includes(searchQuery.toLowerCase());

    return matchesQuery;
  });

  const personalsWithoutAccount = () => {
    const userPersonIds = new Set(
      updateUserDataArr.map((user) => user.personnel_id),
    );

    return personalDataArr.filter(
      (person) => !userPersonIds.has(person.personnel_id),
    );
  };

  // const handleDelete = (item: any) => {

  //   setItemToDelete(item);

  //   setIsDeleteModalOpen(true);

  //   setDeleteConfirmed(false);

  // };

  const confirmDelete = () => {
    if (deleteConfirmed) {
      // Here you would typically make an API call to delete the item

      // console.log("Deleting item:", itemToDelete);

      setIsDeleteModalOpen(false);

      setItemToDelete(null);

      setDeleteConfirmed(false);
    }
  };

  //state and mutation for activate and desactivate user

  //UseReducer for Desactivate, Activate

  //handleling delete & activate & desactivate items

  interface ActionState {
    actionSection: string;

    actionType: string;

    actionText: string;

    currentId: any;
  }

  interface Action {
    type: "DELETE" | "DESACTIVATE" | "CANCEL" | "ACTIVATE";

    payload: { actionText: string; actionSection: string; currentId: any };
  }

  const ACTION_TYPES = {
    DELETE: "DELETE",

    DESACTIVATE: "DESACTIVATE",

    ACTIVATE: "ACTIVATE",

    CANCEL: "CANCEL",
  };

  const INITIAL_STATE: ActionState = {
    actionSection: "",

    actionType: "",

    actionText: "",

    currentId: null,
  };

  const actionReducer = (state: ActionState, action: Action): ActionState => {
    switch (action.type) {
      case ACTION_TYPES.DELETE:
        return {
          ...state,

          actionType: "delete",

          actionSection: action.payload.actionSection,

          actionText: action.payload.actionText,

          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.ACTIVATE:
        return {
          ...state,

          actionType: "activate",

          actionSection: action.payload.actionSection,

          actionText: action.payload.actionText,

          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.DESACTIVATE:
        return {
          ...state,

          actionType: "desactivate",

          actionSection: action.payload.actionSection,

          actionText: action.payload.actionText,

          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.CANCEL:
        return {
          actionSection: "",

          actionType: "",

          actionText: "",

          currentId: null,
        };

      default:
        return state;
    }
  };

  const [state_actions, dispatch] = useReducer(actionReducer, INITIAL_STATE);

  const queryClient = useQueryClient();

  //mutation create user

  const mutationCreateUser = useMutation({
    mutationFn: () =>
      userController.create({
        personnel_id: newUserForm.personalId,

        email: newUserForm.email,

        password: newUserForm.password,

        role_id: newUserForm.roleId,
      }),

    onSuccess: () => {
      setshowNewUserModal(false);

      setNewUserForm({
        id: null,

        personalId: null,

        email: "",

        roleId: "",

        password: "",

        confirmPassword: "",
      });

      showNotification("Création effectuée avec succès", "success");

      queryClient.invalidateQueries({ queryKey: ["users"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  //mutation update user role

  const mutationUpdateRole = useMutation({
    mutationFn: () =>
      userController.update(newUserForm.id, {
        role_id: newUserForm.roleId,
      }),

    onSuccess: (data) => {
      // console.log(data);

      setshowEditUserModal(false);

      setNewUserForm({
        id: null,

        personalId: null,

        email: "",

        roleId: "",

        password: "",

        confirmPassword: "",
      });

      showNotification("Modification effectuée avec succès", "success");

      queryClient.invalidateQueries({ queryKey: ["users"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  // mutation update user password

  const mutationUpdatePassword = useMutation({
    mutationFn: () =>
      userController.updatePassword(newUserForm.id, {
        password: newUserForm.password,
      }),

    onSuccess: (data) => {
      // console.log(data);

      setshowEditPswModal(false);

      setNewUserForm({
        id: null,

        personalId: null,

        email: "",

        roleId: "",

        password: "",

        confirmPassword: "",
      });

      showNotification("Modification effectuée avec succès", "success");

      queryClient.invalidateQueries({ queryKey: ["users"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDesactivate = useMutation({
    mutationFn: () => userController.desactivate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);

      showNotification("Désactivation effectuée avec succès", "success");

      dispatch({
        type: "CANCEL",

        payload: { actionText: "", currentId: null, actionSection: "" },
      });

      // setCurrentId(null);

      queryClient.invalidateQueries({ queryKey: ["users"] });
    },

    onError: (err) => {
      // console.log(err);

      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivate = useMutation({
    mutationFn: () => userController.activate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);

      showNotification("Activation effectuée avec succès", "success");

      dispatch({
        type: "CANCEL",

        payload: { actionText: "", currentId: null, actionSection: "" },
      });

      // setCurrentId(null);

      queryClient.invalidateQueries({ queryKey: ["users"] });
    },

    onError: (err: any) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleActivate = () => {
    if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "user"
    ) {
      mutationActivate.mutate();
    }
  };

  const handleDesactivate = () => {
    if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "user"
    ) {
      mutationDesactivate.mutate();
    }
  };

  //handlers for create, update user and update password

  const handleCreate = () => {
    setNewUserErrors({
      name: "",

      role: "",

      email: "",

      password: "",

      confirmPassword: "",
    });

    const { roleId, email, id, password, confirmPassword } = newUserForm;

    if (!id) {
      setNewUserErrors({
        ...newUserErrors,

        name: "Veuillez sélectionner le personnel",
      });

      return setTimeout(() => {
        setNewUserErrors({
          ...newUserErrors,

          name: "",
        });
      }, 3000);
    }

    if (!roleId) {
      setNewUserErrors({
        ...newUserErrors,

        role: "Veuillez sélectionner le role",
      });

      return setTimeout(() => {
        setNewUserErrors({
          ...newUserErrors,

          role: "",
        });
      }, 3000);
    }

    const zodValidation = createUserSchema.safeParse({
      email,

      password,

      confirmPassword,
    });

    if (!zodValidation.success) {
      const z_errors = z.treeifyError(zodValidation.error);

      setNewUserErrors({
        ...newUserErrors,

        email: z_errors.properties?.email?.errors[0] ?? "",

        password: z_errors.properties?.password?.errors[0] ?? "",

        confirmPassword: z_errors.properties?.confirmPassword?.errors[0] ?? "",
      });

      return setTimeout(() => {
        setNewUserErrors({
          ...newUserErrors,

          email: "",

          password: "",

          confirmPassword: "",
        });
      }, 5000);
    }

    if (password !== confirmPassword) {
      return setNewUserErrors({
        ...newUserErrors,

        confirmPassword: "Les mots de passe ne correspondent pas.",
      });
    }

    mutationCreateUser.mutate();
  };

  const handleUpdateUser = () => {
    setNewUserErrors({
      name: "",

      role: "",

      email: "",

      password: "",

      confirmPassword: "",
    });

    const { roleId } = newUserForm;

    if (!roleId) {
      setNewUserErrors({
        ...newUserErrors,

        role: "Veuillez sélectionner le role",
      });

      return setTimeout(() => {
        setNewUserErrors({
          ...newUserErrors,

          role: "",
        });
      }, 3000);
    }

    mutationUpdateRole.mutate();
  };

  const handleUpdatePassword = () => {
    setNewUserErrors({
      name: "",

      role: "",

      email: "",

      password: "",

      confirmPassword: "",
    });

    const { password, confirmPassword } = newUserForm;

    const zodValidation = updateUserPasswordSchema.safeParse({
      password,

      confirmPassword,
    });

    if (!zodValidation.success) {
      const z_errors = z.treeifyError(zodValidation.error);

      return setNewUserErrors({
        ...newUserErrors,

        password: z_errors.properties?.password?.errors[0] ?? "",

        confirmPassword: z_errors.properties?.confirmPassword?.errors[0] ?? "",
      });
    }

    if (password !== confirmPassword) {
      return setNewUserErrors({
        ...newUserErrors,

        confirmPassword: "Les mots de passe ne correspondent pas.",
      });
    }

    mutationUpdatePassword.mutate();
  };

  const statusPill = (
    active: boolean,
    labelActive: string,
    labelInactive: string,
  ) => (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-semibold ${
        active
          ? isDarkMode
            ? "bg-emerald-500/10 text-emerald-400"
            : "bg-emerald-50 text-emerald-600"
          : isDarkMode
            ? "bg-red-500/10 text-red-400"
            : "bg-red-50 text-red-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-red-500"
        }`}
      ></span>
      {active ? labelActive : labelInactive}
    </span>
  );

  return (
    <>
      {isUserDataLoading || isPersonalDataLoading || isRoleDataLoading ? (
        <UsersManagementSkeleton isDarkMode={isDarkMode} />
      ) : (
        <div>
          <div className="mb-6 flex flex-col justify-start gap-4 lg:flex-row lg:justify-between lg:items-center">
            <div className="flex items-center gap-3">
              <div
                className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <i className="fas fa-users text-sm"></i>
              </div>

              <div>
                <h1 className="text-md font-medium leading-tight">
                  Gestion des utilisateurs
                </h1>

                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Gérez les comptes utilisateurs et leurs accès{" "}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                id="newUserBtn"
                onClick={() => setshowNewUserModal(true)}
                className={`flex items-center px-4 py-2 rounded-md ${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-500 hover:bg-blue-600"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-user-plus mr-2 text-xs"></i>

                <span className="hidden lg:inline text-xs">
                  Nouvel utilisateur
                </span>
              </button>

              {(showNewUserModal || showEditUserModal || showEditPswModal) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                  <div
                    className={`${
                      isDarkMode ? "bg-[#161B22]" : "bg-white"
                    } rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden`}
                  >
                    {/* Header */}

                    <div
                      className={`px-6 py-2 border-b flex items-center gap-3 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isDarkMode
                            ? "bg-blue-500/10 text-blue-400"
                            : "bg-blue-50 text-blue-600"
                        }`}
                      >
                        <i
                          className={`text-xs fas ${
                            showEditUserModal
                              ? "fa-user-shield"
                              : showNewUserModal
                                ? "fa-user-plus"
                                : "fa-key"
                          }`}
                        ></i>
                      </div>

                      <h3 className="text-sm font-semibold leading-tight">
                        {showEditUserModal
                          ? "Modifier le rôle de l'utilisateur"
                          : showNewUserModal
                            ? "Ajouter un utilisateur"
                            : showEditPswModal && "Modifier le mot de passe"}
                      </h3>
                    </div>

                    {/* Form */}

                    <div className="px-6 py-2 space-y-5">
                      <div hidden={showEditPswModal || showEditUserModal}>
                        <label
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Personnels
                        </label>

                        <select
                          required
                          disabled={showEditUserModal}
                          value={newUserForm.personalId}
                          onChange={(e) => {
                            const selectedId = e.target.value;

                            const selected = personalsWithoutAccount().find(
                              (p) => p.personnel_id === parseInt(selectedId),
                            );

                            if (selected) {
                              setNewUserForm((prev) => ({
                                ...prev,

                                id: selected.id,

                                personalId: selectedId,

                                email: selected.email || "",
                              }));
                            } else {
                              setNewUserForm((prev) => ({
                                ...prev,

                                id: null,

                                personalId: null,

                                roleId: "",

                                email: "",
                              }));
                            }
                          }}
                          className={`block w-full h-11 text-[8px] border rounded-lg shadow-sm px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            showEditUserModal
                              ? "opacity-60 cursor-not-allowed"
                              : ""
                          } ${
                            isDarkMode
                              ? "bg-[#0D1117] border-gray-700 text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        >
                          <option value="">Sélectionner un personnel</option>

                          {personalsWithoutAccount()
                            .sort((a, b) => a.nom.localeCompare(b.nom))
                            .map((item) => {
                              return (
                                <option key={item.id} value={item.personnel_id}>
                                  {item.nom.toUpperCase()}{" "}
                                  {item.postnom.toUpperCase()}{" "}
                                  {item.prenom.toUpperCase()}
                                </option>
                              );
                            })}
                        </select>

                        {newUserErrors.name && (
                          <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                            <i className="fas fa-circle-exclamation"></i>

                            {newUserErrors.name}
                          </p>
                        )}
                      </div>

                      {/* <div hidden={showEditUserModal || showEditPswModal}> */}
                      <div hidden={showEditPswModal}>
                        <label
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Email
                        </label>

                        <input
                          type="email"
                          value={newUserForm.email}
                          disabled
                          className={`w-full h-11 px-3 rounded-lg border text-xs transition-colors ${
                            isDarkMode
                              ? "bg-[#0D1117] border-gray-700 text-gray-400"
                              : "bg-gray-50 border-gray-200 text-gray-500"
                          } cursor-not-allowed focus:outline-none`}
                          placeholder="email@example.com"
                        />

                        {newUserErrors.email && (
                          <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                            <i className="fas fa-circle-exclamation"></i>

                            {newUserErrors.email}
                          </p>
                        )}
                      </div>

                      <div hidden={showEditPswModal}>
                        <label
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Rôle
                        </label>

                        <select
                          value={newUserForm.roleId}
                          onChange={(e) =>
                            setNewUserForm({
                              ...newUserForm,

                              roleId: e.target.value,
                            })
                          }
                          className={`w-full h-11 px-3 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-gray-700 text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        >
                          <option value="">Sélectionner un rôle</option>

                          {roleDataArr.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>

                        {newUserErrors.role && (
                          <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                            <i className="fas fa-circle-exclamation"></i>

                            {newUserErrors.role}
                          </p>
                        )}
                      </div>

                      <div hidden={showEditUserModal}>
                        <label
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Mot de passe
                        </label>

                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={newUserForm.password}
                            onChange={(e) => {
                              setNewUserForm({
                                ...newUserForm,

                                password: e.target.value,
                              });

                              setNewUserErrors({
                                ...newUserErrors,

                                password: "",
                              });
                            }}
                            className={`w-full h-11 px-3 pr-10 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                              isDarkMode
                                ? "bg-[#0D1117] border-gray-700 text-gray-100"
                                : "bg-gray-50 border-gray-200 text-gray-900"
                            }`}
                            placeholder="••••••••"
                          />

                          {/* EYE ICON */}

                          <i
                            className={`fas ${
                              showPassword ? "fa-eye-slash" : "fa-eye"
                            } absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 cursor-pointer transition-colors`}
                            onClick={() => setShowPassword(!showPassword)}
                          ></i>
                        </div>

                        {newUserErrors.password && (
                          <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                            <i className="fas fa-circle-exclamation"></i>

                            {newUserErrors.password}
                          </p>
                        )}
                      </div>

                      <div hidden={showEditUserModal}>
                        <label
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Confirmer le mot de passe
                        </label>

                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={newUserForm.confirmPassword}
                            onChange={(e) => {
                              setNewUserForm({
                                ...newUserForm,

                                confirmPassword: e.target.value,
                              });

                              setNewUserErrors({
                                ...newUserErrors,

                                confirmPassword: "",
                              });
                            }}
                            className={`w-full h-11 px-3 pr-10 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                              isDarkMode
                                ? "bg-[#0D1117] border-gray-700 text-gray-100"
                                : "bg-gray-50 border-gray-200 text-gray-900"
                            }`}
                            placeholder="••••••••"
                          />

                          {/* EYE ICON */}

                          <i
                            className={`fas ${
                              showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                            } absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 cursor-pointer transition-colors`}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          ></i>
                        </div>

                        {newUserErrors.confirmPassword && (
                          <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                            <i className="fas fa-circle-exclamation"></i>

                            {newUserErrors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Footer */}

                    <div
                      className={`px-6 py-4 border-t flex justify-end gap-3 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <button
                        disabled={
                          mutationCreateUser.isPending ||
                          mutationUpdateRole.isPending ||
                          mutationUpdatePassword.isPending
                        }
                        onClick={() => {
                          setshowEditUserModal(false);

                          setshowNewUserModal(false);

                          setshowEditPswModal(false);

                          setNewUserForm({
                            id: null,

                            personalId: null,

                            email: "",

                            roleId: "",

                            password: "",

                            confirmPassword: "",
                          });

                          setNewUserErrors({
                            name: "",

                            role: "",

                            email: "",

                            password: "",

                            confirmPassword: "",
                          });
                        }}
                        className={`px-5 py-2.5 rounded-lg text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                          isDarkMode
                            ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-gray-700"
                            : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                        } ${
                          mutationCreateUser.isPending ||
                          mutationUpdateRole.isPending ||
                          mutationUpdatePassword.isPending
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer"
                        }`}
                      >
                        Annuler
                      </button>

                      <button
                        disabled={
                          mutationCreateUser.isPending ||
                          mutationUpdateRole.isPending ||
                          mutationUpdatePassword.isPending
                        }
                        onClick={() => {
                          showEditUserModal
                            ? handleUpdateUser()
                            : showNewUserModal
                              ? handleCreate()
                              : showEditPswModal && handleUpdatePassword();

                          // Here you would typically validate the form and make an API call

                          // console.log("New user:", newUserForm);
                        }}
                        className={`px-5 py-2.5 rounded-lg text-xs font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } ${
                          mutationCreateUser.isPending ||
                          mutationUpdateRole.isPending ||
                          mutationUpdatePassword.isPending
                            ? "cursor-not-allowed opacity-70"
                            : ""
                        }`}
                      >
                        {mutationCreateUser.isPending ||
                        mutationUpdateRole.isPending ||
                        mutationUpdatePassword.isPending ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="fas fa-spinner fa-spin"></i>
                            Chargement...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <i className="fas fa-check"></i>

                            {showEditUserModal
                              ? "Modifier"
                              : showNewUserModal
                                ? "Créer l'utilisateur"
                                : showEditPswModal && "Modifier"}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-6 flex justify-between items-center space-x-2">
            <div className="relative w-64">
              <div
                className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500`}
              >
                <i className="fas fa-search"></i>
              </div>

              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                className={`w-full pl-10 pr-4 py-2 rounded-md text-xs ${
                  isDarkMode
                    ? "bg-[#161B22] border-gray-700"
                    : "bg-white border-gray-300"
                } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div
            className={`rounded-lg overflow-auto border ${
              isDarkMode ? "border-gray-700" : "border-gray-300"
            }`}
          >
            <table
              className={`w-full min-w-[760px] text-xs divide-y ${isDarkMode ? "divide-[#30363D]" : "divide-gray-200"}`}
            >
              <thead
                className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}`}
              >
                <tr>
                  <th
                    scope="col"
                    className={`px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Nom
                  </th>

                  <th
                    scope="col"
                    className={`px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Email
                  </th>

                  <th
                    scope="col"
                    className={`px-4 py-3 text-left ml-[-20px] text-[9px] font-semibold uppercase tracking-[0.08em] ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Rôle
                  </th>

                  <th
                    scope="col"
                    className={`px-4 py-3 text-cnter text-[9px] font-semibold uppercase tracking-[0.08em] ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Statut
                  </th>

                  <th
                    scope="col"
                    className={`px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  ></th>
                </tr>
              </thead>

              <tbody
                className={`${
                  isDarkMode ? "bg-[#0D1117]" : "bg-white"
                } divide-y ${
                  isDarkMode ? "divide-gray-700" : "divide-gray-200"
                }`}
              >
                {filteredUsers
                  .sort((a, b) => a.fullName.localeCompare(b.fullName))
                  .map((item) => {
                    let style1 = "";

                    let style2 = "";

                    switch (item.roleName) {
                      case "Parent":
                        style1 = "bg-purple-500";

                        style2 = "bg-purple-600";

                        break;

                      case "Administrateur":
                        style1 = "bg-red-500";

                        style2 = "bg-red-600";

                        break;

                      case "Comptable":
                        style1 = "bg-green-500";

                        style2 = "bg-green-600";

                        break;

                      case "Enseignant":
                        style1 = "bg-blue-500";

                        style2 = "bg-blue-600";

                        break;

                      default:
                        break;
                    }

                    return (
                      <tr
                        className={`group ${
                          isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
                        }`}
                        key={item.id}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                                isDarkMode ? style2 : style1
                              }



                          text-white`}
                            >
                              <span>{item.initials}</span>
                            </div>

                            <div className="ml-3">
                              <span
                                className={`font-medium ${
                                  isDarkMode ? "text-gray-200" : "text-gray-700"
                                }`}
                              >
                                {item.fullName}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.email}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-lrft">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              isDarkMode
                                ? "bg-blue-900/40 text-blue-400"
                                : "bg-blue-100 text-blue-800"
                            } `}
                          >
                            {item.roleName}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {statusPill(!!item.status, "Actif", "Désactivé")}
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap text-center w-[132px] min-w-[132px]">
                          <div className="flex items-center justify-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity duration-150">
                            <button
                              title="Modifier mot de passe"
                              aria-label="Modifier le mot de passe"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-emerald-500 hover:bg-emerald-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 cursor-pointer"
                              onClick={() => {
                                setNewUserForm({
                                  id: item.id,

                                  email: item.email,

                                  personalId: item.personnel_id,

                                  roleId: item.role_id,

                                  password: "",

                                  confirmPassword: "",
                                });

                                setshowEditPswModal(true);
                              }}
                            >
                              <i className="fas fa-lock"></i>
                            </button>

                            <button
                              title="Modifier rôle"
                              aria-label="Modifier le rôle"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-blue-500 hover:bg-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 cursor-pointer"
                              onClick={() => {
                                setNewUserForm({
                                  id: item.id,

                                  email: item.email,

                                  personalId: item.personnel_id,

                                  roleId: item.role_id,

                                  password: "",

                                  confirmPassword: "",
                                });

                                setshowEditUserModal(true);
                              }}
                            >
                              <i className="fas fa-user-shield"></i>
                            </button>

                            <button
                              title={item.status ? "Désactiver" : "Activer"}
                              onClick={() => {
                                item.status
                                  ? dispatch({
                                      type: "DESACTIVATE",

                                      payload: {
                                        actionText:
                                          "Êtes-vous sûr de vouloir désactiver cet utilisateur?",

                                        currentId: item.id,

                                        actionSection: "user",
                                      },
                                    })
                                  : dispatch({
                                      type: "ACTIVATE",

                                      payload: {
                                        actionText:
                                          "Êtes-vous sûr de vouloir Activer cet utilisateur?",

                                        currentId: item.id,

                                        actionSection: "user",
                                      },
                                    });
                              }}
                              className={`${
                                item.status
                                  ? "text-green-500 hover:text-green-700"
                                  : "text-gray-500 hover:text-green-700"
                              } inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 cursor-pointer`}
                            >
                              <i
                                className={`fa-solid ${item.status ? "fa-circle-xmark" : "fa-circle-check"}`}
                              ></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-between items-center">
            <div
              className={`text-xs ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Affichage de {updateUserDataArr.length} utilisateurs
            </div>
          </div>

          {isDeleteModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-lg p-6 w-full max-w-md`}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <i className="fas fa-exclamation-triangle text-red-500 text-xl"></i>
                  </div>

                  <h3 className="text-xl font-semibold ml-4">
                    Confirmer la suppression
                  </h3>
                </div>

                <p
                  className={`mb-6 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Êtes-vous sûr de vouloir supprimer cet élément ? Cette action
                  est irréversible et toutes les données associées seront
                  définitivement perdues.
                </p>

                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={deleteConfirmed}
                      onChange={(e) => setDeleteConfirmed(e.target.checked)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />

                    <span className="ml-2 text-sm">
                      Je comprends que cette action est irréversible
                    </span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);

                      setItemToDelete(null);

                      setDeleteConfirmed(false);
                    }}
                    className={`px-4 py-2 rounded-md ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-gray-200 hover:bg-gray-300"
                    } !rounded-button whitespace-nowrap`}
                  >
                    Annuler
                  </button>

                  <button
                    onClick={confirmDelete}
                    disabled={!deleteConfirmed}
                    className={`px-4 py-2 rounded-md ${
                      deleteConfirmed
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-red-300 cursor-not-allowed"
                    } text-white !rounded-button whitespace-nowrap`}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Activate dialog */}

          {state_actions.actionType === "activate" && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div
                className={`${
                  isDarkMode ? "bg-[#1c2128]" : "bg-white"
                } rounded-lg shadow-xl w-full max-w-md mx-4 p-6`}
              >
                <div className="text-xl font-medium mb-4">
                  Confirmer l'activation
                </div>

                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>

                <div className="flex justify-end space-x-3">
                  <button
                    disabled={mutationActivate.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",

                        payload: {
                          actionText: "",

                          currentId: "",

                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-md ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-gray-200 hover:bg-gray-300"
                    } !rounded-button whitespace-nowrap ${
                      mutationActivate.isPending ? "cursor-not-allowed" : ""
                    }`}
                  >
                    Annuler
                  </button>

                  <button
                    disabled={mutationActivate.isPending}
                    onClick={handleActivate}
                    className={`px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white !rounded-button whitespace-nowrap ${
                      mutationActivate.isPending ? "cursor-not-allowed" : ""
                    }`}
                  >
                    {mutationActivate.isPending ? (
                      <span className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Activation en cours...
                      </span>
                    ) : (
                      "Confirmer l'activation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desactivate dialog */}

          {state_actions.actionType === "desactivate" && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div
                className={`${
                  isDarkMode ? "bg-[#1c2128]" : "bg-white"
                } rounded-lg shadow-xl w-full max-w-md mx-4 p-6`}
              >
                <div className="text-xl font-medium mb-4">
                  Confirmer la désactivation
                </div>

                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>

                <div className="flex justify-end space-x-3">
                  <button
                    disabled={mutationDesactivate.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",

                        payload: {
                          actionText: "",

                          currentId: "",

                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-md ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-gray-200 hover:bg-gray-300"
                    } !rounded-button whitespace-nowrap ${
                      mutationDesactivate.isPending ? "cursor-not-allowed" : ""
                    }`}
                  >
                    Annuler
                  </button>

                  <button
                    disabled={mutationDesactivate.isPending}
                    onClick={handleDesactivate}
                    className={`px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 text-white !rounded-button whitespace-nowrap ${
                      mutationDesactivate.isPending ? "cursor-not-allowed" : ""
                    }`}
                  >
                    {mutationDesactivate.isPending ? (
                      <span className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Désactivation en cours...
                      </span>
                    ) : (
                      "Confirmer la désactivation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== NOTIFICATION ===== */}

          {notification && (
            <div
              className={`fixed bottom-4 text-[14px] right-4 px-6 py-3 rounded-lg text-white shadow-lg transition-all duration-500 z-[60] ${
                notification.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default User;
